import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import apiService from "../../services/apiService";
import "./InfoTab.css";
import TogglableContentInput from "../ui/TogglableContentInput";

const InfoTab = ({ propertyData }) => {
  const { id } = useParams();
  const [contentItems, setContentItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newContent, setNewContent] = useState({
    contentHeader: "",
    contentBody: "",
    contentWidth: 1,
  });
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);

  // Map state
  const locationAddress = `${propertyData.addressLine2} ${propertyData.addressLine3} ${propertyData.suburb} ${propertyData.province} ${propertyData.country}`;
  const [mapsLocation, setMapsLocation] = useState(locationAddress);
  const [mapPreviewUrl, setMapPreviewUrl] = useState("");
  const [generatedIframe, setGeneratedIframe] = useState("");
  const [addMap, setAddMap] = useState(false);
  const GOOGLE_MAPS_EMBED_KEY = process.env.REACT_APP_GOOGLE_MAPS_EMBED_KEY;

  useEffect(() => {
    fetchContentItems();
  }, [id]);

  const fetchContentItems = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await apiService.get(
        `/property-content-items?propertyId=${id}`
      );
      setContentItems(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching content items:", err);
      setError("Failed to load content items");
    } finally {
      setLoading(false);
    }
  };

  const handleAddContent = async (e) => {
    e.preventDefault();

    if (!newContent.contentHeader || !newContent.contentBody) {
      alert("Please fill in all fields");
      return;
    }

    // Prepend iframe if toggle is ON
    const finalBody = addMap
      ? generatedIframe + newContent.contentBody
      : newContent.contentBody;

    try {
      const maxDisplayOrder =
        contentItems.length > 0
          ? Math.max(...contentItems.map((item) => item.displayOrder))
          : 0;
      const newDisplayOrder = maxDisplayOrder + 1;

      await apiService.post("/property-content-items", {
        userId: 3,
        propertyId: parseInt(id),
        contentHeader: newContent.contentHeader,
        contentBody: finalBody,
        contentWidth: parseInt(newContent.contentWidth),
        displayOrder: newDisplayOrder,
        mapIframe: addMap ? generatedIframe : "",
      });

      alert("Content added successfully!");
      setNewContent({
        contentHeader: "",
        contentBody: "",
        contentWidth: 1,
      });
      fetchContentItems();
    } catch (error) {
      console.error("Error adding content:", error);
      alert("Failed to add content. Please try again.");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewContent({
      ...newContent,
      [name]: value,
    });
  };

  const toggleAccordion = (itemId) => {
    setContentItems((prevItems) =>
      prevItems.map((item) =>
        item.id === itemId ? { ...item, isExpanded: !item.isExpanded } : item
      )
    );
  };

  const handleDeleteContent = async (itemId, e) => {
    e.stopPropagation();
    if (!confirm("Are you sure you want to delete this content item?")) return;

    try {
      await apiService.delete(`/property-content-items/${itemId}`, {
        userId: 3,
      });
      fetchContentItems();
    } catch (error) {
      console.error("Error deleting content:", error);
      alert("Failed to delete content. Please try again.");
    }
  };

  const handleUpdateContent = async (itemId, updatedContent) => {
    try {
      await apiService.put(`/property-content-items/update?id=${itemId}`, {
        userId: 1,
        propertyId: parseInt(id),
        contentHeader: updatedContent.contentHeader,
        contentBody: updatedContent.contentBody,
        contentWidth: updatedContent.contentWidth,
        displayOrder: updatedContent.displayOrder,
        mapIframe: updatedContent.mapIframe ?? "",
      });
      fetchContentItems();
      alert("Content updated successfully!");
    } catch (error) {
      console.error("Error updating content:", error);
      alert("Failed to update content. Please try again.");
    }
  };

  const handleContentChange = (itemId, field, value) => {
    setContentItems((prevItems) =>
      prevItems.map((item) =>
        item.id === itemId ? { ...item, [field]: value } : item
      )
    );
  };

  const handleRemoveMap = async (itemId) => {
    const confirmed = window.confirm(
      "Are you sure you want to remove the map location for this property?"
    );

    if (!confirmed) return;

    const item = contentItems.find((i) => i.id === itemId);
    if (!item) return;

    // Optimistic UI update
    setContentItems((prev) =>
      prev.map((i) => (i.id === itemId ? { ...i, mapIframe: "" } : i))
    );

    try {
      await apiService.put(`/property-content-items/update?id=${itemId}`, {
        userId: 1,
        propertyId: parseInt(id),

        // MUST SEND ALL FIELDS TO AVOID BACKEND RESETTING THEM
        contentHeader: item.contentHeader,
        contentBody: item.contentBody,
        contentWidth: item.contentWidth,
        displayOrder: item.displayOrder,

        // Map removed
        mapIframe: "",
      });
    } catch (err) {
      console.error(err);
      alert("Failed to remove map");

      // Reload from backend to restore correct data
      fetchContentItems();
    }
  };

  // Drag & Drop
  const handleDragStart = (e, item) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.target.outerHTML);
  };

  const handleDragOver = (e, item) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverItem(item);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverItem(null);
  };

  const handleDrop = async (e, targetItem) => {
    e.preventDefault();
    setDragOverItem(null);

    if (!draggedItem || draggedItem.id === targetItem.id) {
      setDraggedItem(null);
      return;
    }

    const draggedIndex = contentItems.findIndex(
      (item) => item.id === draggedItem.id
    );
    const targetIndex = contentItems.findIndex(
      (item) => item.id === targetItem.id
    );

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedItem(null);
      return;
    }

    // Create new array with reordered items
    const newItems = [...contentItems];
    const [removed] = newItems.splice(draggedIndex, 1);
    newItems.splice(targetIndex, 0, removed);

    // Update display orders
    const updatedItems = newItems.map((item, index) => ({
      ...item,
      displayOrder: index + 1,
    }));

    // Find the new display position for the dragged item
    const draggedItemNewPosition =
      updatedItems.find((item) => item.id === draggedItem.id).displayOrder - 1;

    // Update the UI before server changes (optimistic update)
    setContentItems(updatedItems);

    // Call re-order endpoint
    try {
      await apiService.put("/property-content-items/re-order", {
        userId: 3,
        propertyId: parseInt(id),
        propertyContentItemId: draggedItem.id,
        displayOrder: draggedItemNewPosition,
      });
    } catch (error) {
      console.error("Error reordering item:", error);
      alert("Failed to update order. Please refresh the page.");
      fetchContentItems();
    }

    setDraggedItem(null);
  };

  // Map Handlers
  useEffect(() => {
    if (!mapsLocation) return;
    const encoded = encodeURIComponent(mapsLocation);
    setMapPreviewUrl(
      `https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_EMBED_KEY}&q=${encoded}`
    );
  }, [mapsLocation]);

  const generateIframe = (location) => {
    const encoded = encodeURIComponent(location);
    return `<iframe width="600" height="450" style="border:0" loading="lazy" allowfullscreen referrerpolicy="no-referrer-when-downgrade" src="https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_EMBED_KEY}&q=${encoded}"></iframe>`;
  };

  const handleToggleAddMap = (checked) => {
    setAddMap(checked);

    if (checked) {
      const iframe = generateIframe(mapsLocation);
      setGeneratedIframe(iframe);
    } else {
      setGeneratedIframe("");
    }
  };

  const handleSearchAddress = () => {
    if (!mapsLocation) return;

    const encoded = encodeURIComponent(mapsLocation);
    setMapPreviewUrl(
      `https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_EMBED_KEY}&q=${encoded}`
    );

    if (addMap === true) {
      const iframe = generateIframe(mapsLocation);
      setGeneratedIframe(iframe);
    }
  };

  if (loading)
    return (
      <div className="info-content">
        <div className="loading-state">Loading content items...</div>
      </div>
    );
  if (error)
    return (
      <div className="info-content">
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchContentItems} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );

  return (
    <div className="info-content">
      <h3 draggable>Property Information</h3>

      {contentItems.length === 0 ? (
        <div className="no-content">
          No content items found for this property.
        </div>
      ) : (
        <div className="content-accordions">
          {contentItems.map((item) => (
            <div
              key={item.id}
              className={`content-accordion full-width ${
                draggedItem?.id === item.id ? "dragging" : ""
              } ${dragOverItem?.id === item.id ? "drag-over" : ""}`}
              draggable
              onDragStart={(e) => handleDragStart(e, item)}
              onDragOver={(e) => handleDragOver(e, item)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, item)}
            >
              <div
                className="accordion-header"
                onClick={() => toggleAccordion(item.id)}
              >
                <span className="accordion-title">{item.contentHeader}</span>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <span
                    style={{
                      cursor: "pointer",
                      fontSize: "16px",
                      padding: "4px",
                      borderRadius: "4px",
                      transition: "all 0.2s ease",
                      userSelect: "none",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "rgba(239, 68, 68, 0.2)";
                      e.target.style.transform = "scale(1.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "transparent";
                      e.target.style.transform = "scale(1)";
                    }}
                    onClick={(e) => handleDeleteContent(item.id, e)}
                    title="Delete this content item"
                  >
                    🗑️
                  </span>
                  <span className="accordion-icon">
                    {item.isExpanded ? "▼" : "▶"}
                  </span>
                </div>
              </div>

              {item.isExpanded && (
                <div className="accordion-content">
                  <div className="edit-form">
                    <div className="form-row">
                      <label>Header:</label>
                      <input
                        type="text"
                        value={item.contentHeader}
                        onChange={(e) =>
                          handleContentChange(
                            item.id,
                            "contentHeader",
                            e.target.value
                          )
                        }
                        className="form-input"
                      />
                    </div>

                    {/* WYSIWYG Editor */}
                    <div className="form-row">
                      <label>Content Body:</label>
                      <TogglableContentInput
                        value={item.contentBody || ""}
                        onChange={(val) =>
                          handleContentChange(item.id, "contentBody", val)
                        }
                      />
                    </div>

                    {/* --- NEW MAP CHECK SECTION --- */}
                    <div style={{ marginTop: "10px", color: "white" }}>
                      {item.mapIframe ? (
                        <div style={{ display: "flex", gap: "10px" }}>
                          <span>contains map</span>
                          <button
                            style={{
                              background: "red",
                              color: "white",
                              padding: "4px 8px",
                              border: "none",
                              borderRadius: "4px",
                              cursor: "pointer",
                            }}
                            onClick={() => handleRemoveMap(item.id)}
                          >
                            Remove Map
                          </button>
                        </div>
                      ) : (
                        <span>no map to display</span>
                      )}
                    </div>

                    <div className="form-actions">
                      <button
                        type="button"
                        className="add-content-btn"
                        onClick={() => handleUpdateContent(item.id, item)}
                      >
                        Update
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* --- Add New Content Form --- */}
      <div className="add-content-form">
        <h4>Add New Information Block</h4>
        <form onSubmit={handleAddContent}>
          <div className="form-row">
            <label htmlFor="contentHeader">Content Header:</label>
            <input
              type="text"
              id="contentHeader"
              name="contentHeader"
              value={newContent.contentHeader}
              onChange={handleInputChange}
              required
              className="form-input"
              placeholder="Enter content header"
            />
          </div>

          {/* WYSIWYG Editor */}
          <div className="form-row">
            <label>Content Body:</label>
            <TogglableContentInput
              value={newContent.contentBody || ""}
              onChange={(val) =>
                setNewContent({ ...newContent, contentBody: val })
              }
            />
          </div>

          <div className="form-row">
            <label>Location Map Content:</label>

            <div className="map-location-form">
              <input
                type="text"
                name="mapsLocation"
                value={mapsLocation}
                onChange={(e) => setMapsLocation(e.target.value)}
                className="address-input"
                placeholder="Enter an address"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSearchAddress();
                  }
                }}
              />
            </div>

            {mapPreviewUrl && (
              <div className="map-preview-container">
                <iframe
                  title="Google Maps Preview"
                  width="100%"
                  height="200"
                  style={{ border: 0, marginTop: "10px" }}
                  src={mapPreviewUrl}
                  loading="lazy"
                  allowFullScreen
                />
              </div>
            )}

            <label style={{ display: "flex", gap: "6px" }}>
              <input
                type="checkbox"
                checked={addMap}
                onChange={(e) => handleToggleAddMap(e.target.checked)}
              />
              Add Map
            </label>
          </div>

          <div className="form-row">
            <label>Content Width:</label>
            <div className="radio-group">
              <label>
                <input
                  type="radio"
                  name="contentWidth"
                  value="1"
                  checked={newContent.contentWidth == 1}
                  onChange={handleInputChange}
                />{" "}
                Half Width
              </label>
              <label>
                <input
                  type="radio"
                  name="contentWidth"
                  value="2"
                  checked={newContent.contentWidth == 2}
                  onChange={handleInputChange}
                />{" "}
                Full Width
              </label>
            </div>
          </div>

          <button type="submit" className="add-content-btn">
            Add Content
          </button>
        </form>
      </div>
    </div>
  );
};

export default InfoTab;
